/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/RequirementRejectionModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import styles from "./index.module.css";

const RequirementRejectionModal = ({
  dialogRef = null,
  open,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [rejectionReason, setRejectionReason] = useState("");
  useEffect(() => {
    if (dialogRef && dialogRef.current) {
      const dialog = dialogRef.current;
      if (open) {
        setRejectionReason("");
        dialog.showModal();
      } else {
        dialog.close();
      }
    }
  }, [open, dialogRef]);

  useEffect(() => {
    // Cleanup: close dialog when component unmounts
    return () => {
      if (dialogRef && dialogRef.current) {
        dialogRef.current.close();
      }
    };
  }, [dialogRef]);

  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm(rejectionReason);
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <>
      {open && <div className={styles.modalOverlay}></div>}
      <dialog ref={dialogRef} className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>
            {t("components.requirementRejectionModal.title")}
          </h3>
          <button
            className={styles.closeButton}
            onClick={handleCancel}
            disabled={isLoading}
          >
            &times;
          </button>
        </div>

        <div className={styles.modalBody}>
          <p className={styles.modalMessage}>
            {t("components.requirementRejectionModal.message")}
          </p>
          <div className={styles.rejectionReasonContainer}>
            <span className={styles.rejectionReasonLabel}>
              {t("components.requirementRejectionModal.reasonLabel")}
            </span>
            <textarea
              className={styles.rejectionReasonInput}
              placeholder={t(
                "components.requirementRejectionModal.reasonPlaceholder",
              )}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </div>
        </div>

        <div className={styles.modalActions}>
          <button
            className={`${styles.modalButton} ${styles.cancelButton}`}
            onClick={handleCancel}
            disabled={isLoading}
          >
            {t("common.cancel")}
          </button>
          <button
            className={`${styles.modalButton} ${styles.confirmButton}`}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className={styles.loadingText}>{t("common.saving")}</span>
            ) : (
              t("components.requirementRejectionModal.confirmButton")
            )}
          </button>
        </div>
      </dialog>
    </>
  );
};

export default RequirementRejectionModal;
